import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Send, Mic, MicOff, Volume2, ArrowLeft, ChevronRight } from "lucide-react";
import { earnXP } from "../gamification";
import { getSmartResponse, getAllScenarios, resetContext, SCENARIOS } from "../conversationEngine";
import PremiumGate, { isPremium } from "../components/PremiumGate";

export default function ConversationPractice() {
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  if (!isPremium()) return <PremiumGate feature="AI Conversation Practice" />;

  function selectScenario(scenario) {
    resetContext();
    const engine = SCENARIOS[scenario.id];
    const greeting = engine?.greeting || "Hello! Let's practice English together.";
    setSelected({ ...scenario, greeting });
    setMessages([{ type: "ai", text: greeting }]);
    speak(greeting);
    setTimeout(() => inputRef.current?.focus(), 300);
  }

  function sendMessage() {
    if (!input.trim() || !selected) return;
    const userMsg = input.trim();
    setInput("");

    setMessages((prev) => [...prev, { type: "user", text: userMsg }]);

    setIsTyping(true);
    const msgCount = messages.filter((m) => m.type === "user").length + 1;

    setTimeout(() => {
      const response = getSmartResponse(selected.id, userMsg, msgCount);

      // Check if response has grammar tip
      const tipMatch = response.match(/\n\n(💡 Tip:.+)$/);
      const mainResponse = tipMatch ? response.replace(tipMatch[0], "") : response;
      const tip = tipMatch ? tipMatch[1] : null;

      setMessages((prev) => [
        ...prev,
        { type: "ai", text: mainResponse },
        ...(tip ? [{ type: "correction", tip }] : []),
      ]);
      setIsTyping(false);
      speak(mainResponse);

      if (msgCount >= 10) {
        setTimeout(() => {
          setMessages((prev) => [...prev, { type: "system", text: "Great conversation! +25 XP earned 🎉" }]);
          earnXP(25, "conversation");
        }, 1500);
      }
    }, 800 + Math.random() * 1200);
  }

  function startListening() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { alert("Speech recognition not supported. Try Chrome."); return; }
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (e) => { setInput(e.results[0][0].transcript); };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.start();
  }

  function speak(text) {
    if ("speechSynthesis" in window) {
      const u = new SpeechSynthesisUtterance(text.replace(/💡.*$/, "").trim());
      u.rate = Number(localStorage.getItem("speechRate") || "0.85");
      u.lang = "en-US";
      window.speechSynthesis.speak(u);
    }
  }

  // ===== SCENARIO SELECTION =====
  if (!selected) {
    return (
      <div className="min-h-screen bg-paper text-ink-900 full-bleed -mt-6 px-4 sm:px-6 lg:px-8 pt-6 pb-28 md:pb-8 page-enter">
        <Link to="/dashboard" className="text-ink-500 hover:text-ink-700 text-sm font-semibold flex items-center gap-1 mb-6">
          <ArrowLeft size={16} /> Back to Dashboard
        </Link>
        <h1 className="font-display text-4xl md:text-5xl font-normal mb-2"><em className="text-gold-500">AI conversation.</em></h1>
        <p className="text-ink-500 text-sm mb-6">Choose a scenario and practice real English conversations</p>

        <div className="grid sm:grid-cols-2 gap-3">
          {getAllScenarios().map((s, i) => (
            <button key={s.id} onClick={() => selectScenario(s)}
              className={`bg-paper-deep backdrop-blur border border-cream-300 rounded-2xl p-5 text-left hover:bg-paper-deep hover:translate-y-[-4px] hover:shadow-2xl transition-all group stagger-${Math.min(i+1,6)} fade-in`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">{s.emoji}</div>
                  <h3 className="font-black text-sm">{s.title}</h3>
                  <p className="text-ink-500 text-xs mt-1">{s.desc}</p>
                </div>
                <ChevronRight size={18} className="text-ink-400 group-hover:text-ink-500 group-hover:translate-x-1 transition-all" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ===== CHAT INTERFACE =====
  return (
    <div className="h-[calc(100vh-4rem)] md:h-[calc(100vh-5rem)] flex flex-col bg-paper text-ink-900 full-bleed -mt-6 rounded-none">

      {/* HEADER */}
      <div className="flex items-center justify-between p-4 border-b border-cream-200 backdrop-blur bg-paper-deep">
        <div className="flex items-center gap-3">
          <button onClick={() => { setSelected(null); setMessages([]); }} className="p-1.5 hover:bg-paper-deep rounded-lg transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sage to-sage-dark flex items-center justify-center text-lg shadow-lg">
            {selected.emoji}
          </div>
          <div>
            <p className="font-black text-sm">{selected.title}</p>
            <p className="text-xs text-sage-light flex items-center gap-1">
              <span className="w-2 h-2 bg-sage-light rounded-full animate-pulse" /> Online
            </p>
          </div>
        </div>
        <div className="text-xs bg-paper-deep px-3 py-1 rounded-full font-bold">
          {messages.filter(m => m.type === "user").length} / 10
        </div>
      </div>

      {/* CHAT AREA */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((msg, i) => {
          if (msg.type === "ai") {
            return (
              <div key={i} className="flex gap-2 fade-in">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sage to-sage-dark flex items-center justify-center text-sm flex-shrink-0 shadow">
                  {selected.emoji}
                </div>
                <div className="bg-paper-deep backdrop-blur px-4 py-3 rounded-2xl rounded-bl-sm max-w-[80%] text-sm leading-relaxed">
                  {msg.text}
                  <button onClick={() => speak(msg.text)} className="block mt-1.5 text-ink-400 hover:text-ink-500 transition-colors">
                    <Volume2 size={14} />
                  </button>
                </div>
              </div>
            );
          }

          if (msg.type === "user") {
            return (
              <div key={i} className="flex justify-end fade-in">
                <div className="bg-gradient-to-r from-ink-900 to-gold-600 px-4 py-3 rounded-2xl rounded-br-sm max-w-[80%] text-sm leading-relaxed shadow-lg shadow-gold-1000/20">
                  {msg.text}
                </div>
              </div>
            );
          }

          if (msg.type === "correction") {
            return (
              <div key={i} className="bg-gold-500/10 border border-gold-500/30 p-4 rounded-2xl mx-8 fade-in">
                <p className="text-xs text-gold-300/80 font-bold">{msg.tip}</p>
              </div>
            );
          }

          if (msg.type === "system") {
            return (
              <div key={i} className="text-center bounce-in">
                <span className="bg-sage/20 border border-sage/30 text-sage-light text-sm font-bold px-4 py-2 rounded-full">
                  {msg.text}
                </span>
              </div>
            );
          }

          return null;
        })}

        {/* TYPING INDICATOR */}
        {isTyping && (
          <div className="flex gap-2 fade-in">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sage to-sage-dark flex items-center justify-center text-sm flex-shrink-0">
              {selected.emoji}
            </div>
            <div className="bg-paper-deep backdrop-blur px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1.5 items-center">
              <span className="w-2 h-2 bg-white/60 rounded-full typing-dot" />
              <span className="w-2 h-2 bg-white/60 rounded-full typing-dot" />
              <span className="w-2 h-2 bg-white/60 rounded-full typing-dot" />
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* INPUT AREA */}
      <div className="p-4 border-t border-cream-200 bg-paper-deep backdrop-blur">
        <div className="flex items-center gap-2">
          <button onClick={startListening}
            className={`p-3 rounded-full transition-all ${
              isListening ? "bg-berry mic-pulse" : "bg-paper-deep hover:bg-cream-200 hover:scale-110"
            }`}>
            {isListening ? <MicOff size={20} /> : <Mic size={20} />}
          </button>

          <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Type your message..."
            className="flex-1 bg-paper-deep border border-cream-200 focus:border-cream-300 px-4 py-3 rounded-xl outline-none placeholder:text-ink-400 text-sm font-medium transition-colors" />

          <button onClick={sendMessage} disabled={!input.trim()}
            className="p-3 rounded-full bg-gradient-to-r from-ink-900 to-gold-600 hover:scale-110 active:scale-95 transition-all shadow-lg shadow-gold-1000/30 disabled:opacity-30 disabled:hover:scale-100">
            <Send size={18} />
          </button>
        </div>

        {isListening && (
          <p className="text-center text-xs text-berry-light mt-2 animate-pulse">🎤 Listening... Speak now!</p>
        )}
      </div>
    </div>
  );
}
